import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  chunkError: boolean;
  otherError: Error | null;
}

const RELOAD_KEY = "vrg_chunk_reload";

function isChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("Loading chunk") ||
    error.name === "ChunkLoadError"
  );
}

/**
 * Catches lazy-chunk loading failures (e.g. after a new deployment flushes
 * the old bundle hashes). On first occurrence, unregisters the service worker
 * and does a hard reload. Uses sessionStorage to avoid infinite reload loops.
 *
 * Non-chunk errors are re-thrown in render() so they propagate to AppErrorBoundary.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { chunkError: false, otherError: null };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    if (isChunkError(error)) return { chunkError: true, otherError: null };
    // Store non-chunk errors so we can re-throw in render()
    return { chunkError: false, otherError: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkError(error)) return;

    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (alreadyReloaded) return;

    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      });
    }

    if ("caches" in window) {
      caches.keys().then((names) => {
        Promise.all(names.map((n) => caches.delete(n))).then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
  }

  render() {
    // Non-chunk error: re-throw so AppErrorBoundary (parent) catches it
    if (this.state.otherError) {
      throw this.state.otherError;
    }

    if (this.state.chunkError) {
      // Do NOT remove RELOAD_KEY here — componentDidCatch checks it AFTER render.
      // Only remove it when the user clicks Reload.
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="max-w-sm text-center space-y-4">
            <h1 className="text-xl font-semibold">Update available</h1>
            <p className="text-sm text-muted-foreground">
              A new version was deployed. Click below to load it.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem(RELOAD_KEY);
                window.location.reload();
              }}
              className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
